import { getExhibitionKakaoTemplate } from '@bucketplace/common';
import { BREAK_POINT, MEDIA_QUERY } from '@bucketplace/design-system';
import { GridItemProps } from '@bucketplace/design-system/bds';
import { useScrollIntoAnchor } from '@bucketplace/jsx-editor/exhibition';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { useNavigationMenu } from '@/hooks';
import { cssColumn } from '@/styles/grids';
import {
  PromotionDetailSection,
  SECTION_ITEMS_TAB,
  SECTION_PARAM,
} from '../types';
import { ExhibitionSectionItems } from './ExhibitionSectionItems';
import { SectionNavigation } from './SectionNavigation';
import { SectionViewer } from './SectionViewer';

interface AnchorInfo {
  targetId?: string;
  isInitialAnchor: boolean;
}

interface PromotionsSectionTemplateProps {
  // 기획전 에디터 정보
  units: string;
  // promotion (기획전) id
  promotionId: string;
  // 기획전 타이틀
  title: string;
  // 기획전 커버이미지
  coverImageUrls: string[];
  // 기획전 > section > detail id
  detailId: string;
  // 앵커링 target, disable 정보
  anchorInfo: AnchorInfo;
  isLoading: boolean;
  initialSection?: string;
  sections: PromotionDetailSection[];
  // 상품 목록 스티키 탭 적용 여부
  stickyTab?: boolean;
  onViewerLoaded: (isLoaded: boolean) => void;
}

export function PromotionsSectionTemplate({
  units,
  promotionId,
  title,
  coverImageUrls,
  detailId,
  anchorInfo,
  isLoading,
  initialSection,
  sections,
  stickyTab,
  onViewerLoaded,
}: PromotionsSectionTemplateProps): ReactElement {
  const [sectionTitle, setSectionTitle] = useState<string | null>(
    initialSection ?? null,
  );
  const totalSections = sections.filter((section) => section?.title) ?? [];
  const sectionId =
    totalSections.find((section) => section.title === sectionTitle)?.id ?? '';

  const router = useRouter();
  const { data: navigationMenu, error } = useNavigationMenu(router.pathname);
  const navigationLoaded = !(navigationMenu == null && !error);

  const { containerRef, scrollToTarget, anchorEnd } = useScrollIntoAnchor({
    targetId: anchorInfo.targetId ?? SECTION_ITEMS_TAB,
    isLoading: isLoading || !navigationLoaded,
    initialAnchor: anchorInfo.isInitialAnchor,
  });

  const kakaoTemplate = useMemo(
    () =>
      getExhibitionKakaoTemplate({
        exhibitionId: promotionId,
        title,
        coverImageUrls: coverImageUrls ?? [],
        products: [],
      }),
    [coverImageUrls, promotionId, title],
  );

  const handleEditorClick = useCallback(
    (targetId: string, smooth?: boolean) => {
      if (targetId.includes(SECTION_PARAM)) {
        setSectionTitle(targetId.replace(SECTION_PARAM, ''));
        scrollToTarget(SECTION_ITEMS_TAB);
      } else {
        scrollToTarget(targetId, smooth);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleNavigationClick = useCallback(
    (title: string, index?: number) => {
      setSectionTitle(index === -1 ? null : title);
      scrollToTarget(SECTION_ITEMS_TAB);
    },
    [scrollToTarget],
  );

  return (
    <Container
      ref={(ref) => {
        if (anchorEnd) {
          containerRef.current = null;
        } else {
          containerRef.current = ref;
        }
      }}
    >
      <GridItem
        column={{
          mobile: 'full',
          tablet: 'full',
          laptop: 9,
        }}
      >
        <SectionViewer
          exhibitionId={promotionId}
          units={units}
          // TODO: 서버에서 데이터 가져오기
          kakaoTemplate={kakaoTemplate}
          experimentType={null}
          onViewerLoaded={onViewerLoaded}
          onClick={handleEditorClick}
        />
        <div id={SECTION_ITEMS_TAB} />
        {stickyTab && (
          <SectionNavigation
            sectionTitle={sectionTitle}
            sections={totalSections}
            onClick={handleNavigationClick}
          />
        )}
        <Content hasStickyTab={stickyTab ?? true}>
          <ExhibitionSectionItems
            id={promotionId}
            detailId={detailId}
            sectionId={sectionId}
          />
        </Content>
      </GridItem>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: ${BREAK_POINT.desktop}px;
  margin: 0 auto;
  padding: 30px 60px;
  ${MEDIA_QUERY.max(
    'tablet',
    `
    padding: 0;
  `,
  )}
`;

const GridItem = styled.div<{ column: GridItemProps['column'] }>`
  position: relative;
  margin: 0 auto;
  ${({ column }) => cssColumn(column)};
`;

const Content = styled.div<{ hasStickyTab: boolean }>`
  ${({ hasStickyTab }) =>
    hasStickyTab &&
    css`
    padding-top: 40px;
    ${MEDIA_QUERY.max(
      'tablet',
      css`
      padding-top: 24px;
    `,
    )}
  `};
`;
